import type { ApsViewable } from "../stores/accModelStore";

export async function ensureAccDocumentViewable(
  doc: Autodesk.Viewing.Document,
  viewableId: string | null
): Promise<Autodesk.Viewing.BubbleNode | null> {
  const root = doc.getRoot();
  const nodes = root.search({ type: "geometry" }) ?? [];
  if (viewableId) {
    const match = nodes.find(n => {
      const data = n.data as { guid?: string };
      const id = data.guid ?? n.guid?.();
      return id === viewableId;
    });
    if (match) return match;
  }
  return root.getDefaultGeometry();
}

/** Score 3D viewables — prefer building-wide views over personal {3D - user} views. */
export function pick3DViewableId(viewables: ApsViewable[]): string | null {
  const threeDs = viewables.filter(v => v.kind === "3d");
  if (threeDs.length === 0) return null;

  const score = (v: ApsViewable): number => {
    const n = v.name.toLowerCase();
    if (/new\s*construction|whole\s*building|building\s*model/i.test(n)) {
      return 100;
    }
    if (/^3d\s*view\s*\d*$/i.test(n.trim()) || n === "3d" || n === "{3d}") {
      return 75;
    }
    if (/master|architectural|complete/i.test(n)) return 70;
    if (/^\{?\s*3d\s*-\s*[^}]+\}?$/i.test(v.name.trim())) return 5;
    return 40;
  };

  return [...threeDs].sort((a, b) => score(b) - score(a))[0]?.id ?? null;
}

export function waitForGeometryLoaded(
  viewer: Autodesk.Viewing.GuiViewer3D
): Promise<void> {
  return new Promise(resolve => {
    const onGeom = () => {
      viewer.removeEventListener(
        Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
        onGeom
      );
      resolve();
    };
    viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeom);
  });
}
